import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { rooms } from '../data/siteData';

function RoomImageGallery({ room, flipped }) {
    const [activeIdx, setActiveIdx] = useState(0);
    return (
        <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                    src={room.images[activeIdx]}
                    alt={`${room.name} - view ${activeIdx + 1}`}
                    className="w-full h-full object-cover transition-opacity duration-500"
                />
            </div>
            {room.images.length > 1 && (
                <div className="flex gap-2 mt-3">
                    {room.images.map((img, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveIdx(i)}
                            className={`flex-1 aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all duration-300 ${i === activeIdx
                                    ? 'border-gold-500 shadow-lg shadow-gold-500/20'
                                    : 'border-transparent opacity-60 hover:opacity-100'
                                }`}
                        >
                            <img src={img} alt={`${room.name} thumb ${i + 1}`} className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
            <div className={`absolute -bottom-4 ${flipped ? '-left-4' : '-right-4'} w-full h-[calc(100%-2.5rem)] top-0 rounded-2xl border-2 border-gold-500/20 -z-10`} />
        </div>
    );
}

export default function RoomsPage() {
    return (
        <main>
            {/* Hero */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <img src="/images/rooms/luxury-1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
                <div className="relative z-10 container-custom text-center px-4">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
                        <span className="text-white/80 font-sans text-xs tracking-widest uppercase">Stay With Us</span>
                    </div>
                    <h1 className="heading-display text-white mb-4">
                        Our <span className="italic text-gold-300">Rooms</span>
                    </h1>
                    <p className="font-body text-xl text-white/60 max-w-xl mx-auto">
                        Each room is thoughtfully designed to offer the perfect balance of comfort and mountain charm.
                    </p>
                </div>
            </section>

            {/* Rooms Detail */}
            <section className="section-padding">
                <div className="container-custom space-y-24">
                    {rooms.map((room, index) => (
                        <AnimatedSection key={room.id}>
                            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start ${index % 2 !== 0 ? 'lg:direction-rtl' : ''
                                }`}>
                                {/* Image */}
                                <div className={`${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
                                    <RoomImageGallery room={room} flipped={index % 2 !== 0} />
                                </div>

                                {/* Content */}
                                <div className={`${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-sans font-semibold tracking-wider uppercase ${room.type === 'luxury'
                                            ? 'bg-gold-500/10 text-gold-600 dark:text-gold-400'
                                            : 'bg-forest-500/10 text-forest-600 dark:text-forest-400'
                                            }`}>
                                            {room.type === 'luxury' ? '✦ Luxury' : '◆ Bunker'}
                                        </span>
                                        <span className="text-xs font-sans text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                                            </svg>
                                            {room.capacity}
                                        </span>
                                    </div>

                                    <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                                        {room.name}
                                    </h2>
                                    <p className="font-body text-lg italic text-gold-600 dark:text-gold-400 mb-6">
                                        {room.tagline}
                                    </p>
                                    <p className="font-sans text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                                        {room.description}
                                    </p>

                                    {/* Features */}
                                    <div className="mb-8">
                                        <h4 className="font-sans text-xs font-semibold tracking-widest uppercase text-gray-500 dark:text-gray-400 mb-4">
                                            Room Features
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            {room.features.map(feature => (
                                                <div key={feature} className="flex items-center gap-2.5">
                                                    <svg className="w-4 h-4 text-gold-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="font-sans text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <Link
                                        to="/contact"
                                        className="btn-primary text-sm"
                                    >
                                        Book {room.name} →
                                    </Link>
                                </div>
                            </div>
                        </AnimatedSection>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="section-padding bg-gray-50 dark:bg-royal-600/20">
                <div className="container-custom text-center">
                    <AnimatedSection>
                        <h2 className="heading-section text-gray-900 dark:text-white mb-4">
                            Can't <span className="italic text-gold-600 dark:text-gold-400">Decide</span>?
                        </h2>
                        <p className="text-body-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto mb-8">
                            Reach out to us and we'll help you pick the perfect room for your stay.
                            Group bookings and special arrangements available.
                        </p>
                        <Link to="/contact" className="btn-primary">
                            Get in Touch →
                        </Link>
                    </AnimatedSection>
                </div>
            </section>
        </main>
    );
}
